import { createMemo, untrack, type Accessor } from 'solid-js'
import { produce, unwrap, type SetStoreFunction } from 'solid-js/store'
import type { SelectionContext } from '../canvas/selection'
import type { CanvasContext } from '../canvas/canvas'
import type { UploadedImageData } from '../types'
import { assignIds, migrateGeometry, needsGeometryMigration, findObjectById, findParentOf, findSiblingArray, rectsOverlap } from '../canvas/utils'
import type { RuntimeObject, RuntimeText, RuntimeImage, RuntimeContainer, Box, Dimensions } from '../types'
import type { ImplRegistry } from '../canvas/interactions/ObjectImpl'
import { fitAddedChild, fieldReserveWidth, placedContainerLeft, ADD_TEXT_DEF_W, DEFAULT_FONT, type AddFit } from '../canvas/container/ContainerImpl'
import type { Position, Size, Style, Canvas } from './posterSchema'
import type { DesignLinkContext } from './designLink'
import { useUser } from 'Shared/SiteProvider'

export interface ObjectContext {
	objects: Accessor<RuntimeObject[]>
	setObjects: SetStoreFunction<RuntimeObject[]>
	findParentOf: (id: string) => string | null
	getObject: (id: string) => RuntimeObject | null
}

// The fallback for a design carrying no objects at all. Module-level (not a fresh []) so the array
// a reader sees keeps a stable identity: EditablePosterSvg keys the user-mode bake on it, and a new
// array on every read would re-key it endlessly.
const NO_OBJECTS: RuntimeObject[] = []

export function createObjectState(
	sourceObjects: () => RuntimeObject[] | undefined,
	setSource:     SetStoreFunction<RuntimeObject[]> | undefined,
): ObjectContext {
	// Editable hosts pass the caller's live store slice plus a setter, so the builder edits the
	// caller's store directly. Read-only hosts (viewers) pass only a value and never write, so we
	// derive a private clone — assignIds mutates it, and it never writes back anywhere. It's a memo,
	// not a one-shot snapshot, so a caller swapping in a new design (e.g. an async load) reseeds the
	// view; the memo cache keeps the clone (and so its ids) stable across reads within a render.
	const readOnly = !setSource
	const clone = readOnly ? createMemo(() => seedReadOnly(sourceObjects())) : null
	const objects = () => clone ? clone() : (sourceObjects() ?? NO_OBJECTS)
	const setObjects = setSource ?? ((() => {}) as unknown as SetStoreFunction<RuntimeObject[]>)

	// Ids are now persisted, so a design saved by this builder reloads with its ids intact and
	// everything keyed by id (selection, the impl registry, delete) keeps working. Legacy designs
	// saved before ids were persisted — and AI-authored ones — arrive without them, so assignIds
	// fills the gaps: the read-only branch on its private clone above, the editable store in here.
	// Untracked so seeding never feeds back into the caller's builder memo.
	if (!readOnly) {
		untrack(() => seedEditable(sourceObjects(), setObjects))
	}

	return {
		objects,
		setObjects,
		findParentOf: (id) => findParentOf(id, objects()),
		getObject:    (id) => findObjectById(objects(), id),
	}
}

function seedReadOnly(objects?: RuntimeObject[]): RuntimeObject[] {
	const clone = structuredClone(unwrap(objects ?? [])) as RuntimeObject[]
	migrateGeometry(clone)
	assignIds(clone)
	return clone
}

// Only writes to the store when the design actually needs seeding — either missing ids or carrying a
// legacy { left, top } position. Both passes are idempotent, so running them on an already-seeded
// design is a no-op; the guard just avoids a needless produce write (and store churn) on every load.
function seedEditable(objects: RuntimeObject[] | undefined, setObjects: SetStoreFunction<RuntimeObject[]>): void {
	if (!objects?.length || (!missingIds(objects) && !needsGeometryMigration(objects))) return
	setObjects(produce((arr: RuntimeObject[]) => { migrateGeometry(arr); assignIds(arr) }))
}

function missingIds(objects: RuntimeObject[]): boolean {
	return objects.some(obj => !obj.id
		|| (obj.type === 'container' && !!obj.objects?.length && missingIds(obj.objects)))
}

export type ObjectType = 'text' | 'image' | 'container'

let addTracking: Record<string, { x: number; y: number }> = {}

function getObjectBBox(obj: RuntimeObject, impls: ImplRegistry, canvas: () => Canvas): Box {
	const reg = impls[obj.id]
	if (reg?.getBBox) return reg.getBBox()
	const p = obj.position
	const s = (obj as RuntimeImage | RuntimeContainer).size
	const w = canvas().width  ?? 99999
	const h = canvas().height ?? 99999
	if (p) return { pos: { x: p.x ?? 0, y: p.y ?? 0 }, dims: { width: s?.width ?? w, height: s?.height ?? h } }
	return { pos: { x: 0, y: 0 }, dims: { width: w, height: h } }
}

// The inner space a container has for a newly added child, per its layout: absolute fits within the
// padded box; the flex modes reserve the height already used by existing children (measured live).
// Handed to fitAddedChild, which scales the child down to this box, floored at the addition minimum.
function computeAddFit(
	type: ObjectType, isField: boolean, container: RuntimeContainer,
	targetW: number, targetH: number, impls: ImplRegistry,
): AddFit {
	const MARGIN = 10
	const mode = container.layout ?? 'absolute'
	const gap  = container.gap ?? 10
	const kids = (container.objects ?? []).map(o => impls[o.id]?.getSize?.() ?? { width: 0, height: 0 })
	const usedH = kids.reduce((sum, k) => sum + k.height, 0)

	let availW: number, availH: number
	if (mode === 'absolute') {
		availW = targetW - 2 * MARGIN
		availH = targetH - 2 * MARGIN
	} else if (mode === 'top' || mode === 'center' || mode === 'bottom') {
		availW = targetW
		availH = targetH - usedH - gap * Math.max(0, kids.length - 1) - (kids.length ? gap : 0)
	} else {
		// between / around / evenly distribute free space rather than a fixed gap (see computeFlexColumn).
		availW = targetW
		availH = targetH - usedH
	}
	return fitAddedChild(type, isField, availW, availH)
}

// When even the floored child is wider than the container, widen the container to admit it — there is
// no horizontal auto-grow (vertical growth is handled reactively by effectiveHeight). The left edge is
// re-anchored and clamped inside the container's own parent (or the canvas) so it stays in bounds.
// `arr` is the live produce draft; `container` is the draft node just mutated.
function widenContainerForChild(
	arr: RuntimeObject[], container: RuntimeContainer, selId: string,
	fit: AddFit, childX: number, objCtx: ObjectContext, impls: ImplRegistry, canvasW: number,
): void {
	const MARGIN = 10
	const mode = container.layout ?? 'absolute'
	const kidWidths = (container.objects ?? []).map(o => impls[o.id]?.getSize?.()?.width ?? 0)
	const requiredW = mode === 'absolute'
		? childX + fit.contentW + MARGIN
		: Math.max(fit.contentW, ...kidWidths)
	const parentId  = objCtx.findParentOf(selId)
	const parentObj = parentId ? findObjectById(arr, parentId) as RuntimeContainer | null : null
	const parentW   = (parentId ? impls[parentId]?.getSize?.()?.width : undefined)
		?? parentObj?.size?.width ?? canvasW
	const storedW = container.size?.width ?? canvasW
	const newW    = Math.max(storedW, requiredW)
	container.size     = { ...(container.size ?? {}), width: newW }
	container.position = { x: placedContainerLeft(parentW, container.position?.x ?? 0, storedW, newW), y: container.position?.y ?? 0 }
}

export function addObject(objCtx: ObjectContext, selCtx: SelectionContext, canvasCtx: CanvasContext) {
	const userCtx    = useUser();

	return (type: ObjectType, isField?: boolean): string => {
		const objects = objCtx.objects()
		const setObjects = objCtx.setObjects
		const w = canvasCtx.canvas().width  ?? 400
		const h = canvasCtx.canvas().height ?? 600
		const selId  = selCtx.selected()
		const selObj = selId ? findObjectById(objects, selId) : null
		const isContainer = selObj?.type === 'container'

		const OFFSET = 20, MARGIN = 10
		const sizes: Record<ObjectType, [number, number]> = { text: [200, 40], image: [160, 120], container: [200, 100] }
		const [objW, objH] = sizes[type]

		let targetKey: string, targetW: number, targetH: number
		if (isContainer && selId) {
			const live = selCtx.impls[selId]?.getBBox?.(!!userCtx)
			targetKey = selId
			targetW   = live?.dims.width ?? (selObj as RuntimeContainer).size?.width  ?? w
			targetH   = live?.dims.height ?? (selObj as RuntimeContainer).size?.height ?? h
		} else {
			targetKey = 'poster'; targetW = w; targetH = h
		}

		// When adding into a container, scale the new child down to the space the container's current
		// layout has for it (floored at a small "addition" size). Top-level adds keep the default size.
		const fit = isContainer && selId
			? computeAddFit(type, !!isField, selObj as RuntimeContainer, targetW, targetH, selCtx.impls)
			: null

		const last = addTracking[targetKey]
		let x = last ? last.x + OFFSET : MARGIN
		let y = last ? last.y + OFFSET : MARGIN
		if (x + objW > targetW - MARGIN) x = MARGIN
		if (y + objH > targetH - MARGIN) y = MARGIN
		addTracking[targetKey] = { x, y }

		const position: Position = { x, y }
		const size: Size         = { width: fit?.size?.width ?? objW, height: fit?.size?.height ?? objH }
		const id = `${type}-${Date.now()}`

		let newObj: RuntimeObject
		if (type === 'text') {
			const style: Style = { color: '#ffffff', fontSize: 24, fontWeight: 700, fontFamily: 'Roboto' }
			if (isField) style.isField = true
			const txt: RuntimeText = { type: 'text', id, position, content: 'New Text', style, alignment: 'center' }
			if (isField) txt.size = { width: fit?.size?.width ?? objW }
			newObj = txt
		} else if (type === 'image') {
			const img: RuntimeImage = { type: 'image', id, position, size, isField: isField ?? false }
			newObj = img
		} else {
			newObj = { type: 'container', id, position, size } as RuntimeContainer
		}

		if (isContainer && selId) {
			setObjects(produce((arr: RuntimeObject[]) => {
				const container = findObjectById(arr, selId) as RuntimeContainer | null
				if (!container) return
				if (!container.objects) container.objects = []
				container.objects.push(newObj)
				if (fit?.needGrowW) widenContainerForChild(arr, container, selId, fit, x, objCtx, selCtx.impls, w)
			}))
		} else {
			setObjects(produce((arr: RuntimeObject[]) => arr.push(newObj)))
		}
		selCtx.select(id)
		return id
	}
}

export function removeObjectById(objCtx:ObjectContext, id: string): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		function removeFrom(list: RuntimeObject[]): boolean {
			const i = list.findIndex(o => o.id === id)
			if (i >= 0) { list.splice(i, 1); return true }
			for (const obj of list) {
				if (obj.type === 'container' && obj.objects && removeFrom(obj.objects)) return true
			}
			return false
		}
		removeFrom(arr)
	}))
}

export function restoreObject(objCtx:ObjectContext, snapshot: RuntimeObject, parentId: string | null): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		if (parentId) {
			const parent = findObjectById(arr, parentId) as RuntimeContainer | null
			if (parent) {
				if (!parent.objects) parent.objects = []
				parent.objects.push(snapshot)
			}
		} else {
			arr.push(snapshot)
		}
	}))
}

// Moves a child within its container's objects array so it ends at array position toIndex.
// Used by vertical drag-reorder in flex-layout containers, where array order drives the
// laid-out order. Idempotent: re-running with the child already at toIndex is a no-op.
export function moveChildToIndexInStore(objCtx:ObjectContext, parentId: string, childId: string, toIndex: number): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const parent = findObjectById(arr, parentId) as RuntimeContainer | null
		if (!parent?.objects) return
		const from = parent.objects.findIndex(o => o.id === childId)
		if (from < 0) return
		const [moved] = parent.objects.splice(from, 1)
		const clamped = Math.max(0, Math.min(toIndex, parent.objects.length))
		parent.objects.splice(clamped, 0, moved)
	}))
}

// Bake the linked-design layout into the flat store: write each linked object's design-computed
// absolute position, so a save/capture persists the record-responsive layout. The user-mode view
// already renders these positions live (via design.laidOut); this is only the persistence step, run
// from AutoSave's save event and from the geometry settle — never a standing effect. One produce so
// subscribers see a single update. Returns whether anything actually moved or resized, so a caller
// can save on a real reflow and stay quiet otherwise.
//
// Containers also get their laid-out *size*. A hoisted background shape has no children left to grow
// it, so its stored size is only the designed minimum the template started from; without this a panel
// that grew to fit its content would be saved short, and anything rendering the design without the
// shadow — the public frontend, which builds unlinked — would draw it that way. Leaves are positions
// only: their sizes come from their own content (text re-wraps, a field keeps its stored width), and
// they are the input the layout was computed from, not its output.
//
// The box the layout hands back is the box it *placed* — which for a hugging field is neither the box
// it draws nor the box the store holds. Two conversions, the same pair detachIfLinked runs: displayBox
// shrink-wraps the placed box onto the ink the field actually draws, and storedPositionOf then strips
// the alignment insets (horizontal per textAlign, vertical per the field's alignment) exactly as a drag
// commit does. What lands here is then what every reader of the stored position — the fit's re-anchor,
// export, and the unlinked frontend render (position + stored width, text anchored within it) — expects.
// Writing the placed position straight in instead made the next re-anchor add the insets a second time.
export function bakeLinkedPositions(objCtx: ObjectContext, impls: ImplRegistry, design: DesignLinkContext): boolean {
	let wrote = false
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		for (const id of design.linkedIds()) {
			const box = design.laidOut(id)
			if (!box) continue
			const obj = findObjectById(arr, id)
			if (!obj) continue
			const impl   = impls[id]
			const drawn  = impl ? impl.displayBox(box) : box
			const stored = impl ? impl.storedPositionOf(drawn.pos) : drawn.pos
			const pos = { x: Math.round(stored.x), y: Math.round(stored.y) }
			/* Only on a real move: a same-valued write still replaces the store node, and every deep
			   reader of the design (PosterRenderer's capture memo, the event page's change tracking)
			   would take it as an edit — a settle→bake→re-render→re-fit→settle loop. */
			if (obj.position?.x !== pos.x || obj.position?.y !== pos.y) {
				obj.position = pos
				wrote = true
			}

			if (obj.type !== 'container') continue
			const container = obj as RuntimeContainer
			const dims = { width: Math.round(box.dims.width), height: Math.round(box.dims.height) }
			if (container.size?.width !== dims.width || container.size?.height !== dims.height) {
				container.size = dims
				wrote = true
			}
		}
	}))
	return wrote
}

// Detach a leaf from the linked template layout, or (slot === null) put it back under the template's
// control. The flag lives in the design itself rather than in a runtime signal so a drag survives a
// reload: on load designLink reads it back, and the leaf renders from its own stored position instead
// of being laid out — and re-baked — off the template. The slot size is what the template reserves in
// its place; see designLink's detached map and toDummy.
export function setDetachedInStore(objCtx:ObjectContext, id: string, slot: Dimensions | null): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id)
		if (!obj) return
		if (slot === null) delete obj.detached
		else obj.detached = { width: slot.width, height: slot.height }
	}))
}

export function setObjectPositionSize(objCtx:ObjectContext, id: string, position?: Partial<Position>, size?: Partial<Size> | null): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id)
		if (!obj) return
		if (position !== undefined) obj.position = { x: position.x ?? obj.position?.x ?? 0, y: position.y ?? obj.position?.y ?? 0 }
		const sized = obj as RuntimeImage | RuntimeContainer | RuntimeText
		if (size === null) delete (sized as RuntimeContainer).size
		else if (size !== undefined) sized.size = { ...(sized.size ?? {}), ...size }
	}))
}

export function applyStylePatch(objCtx:ObjectContext, id: string, patch: Style): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id)
		if (obj) {
			const styled = obj as RuntimeText | RuntimeContainer | RuntimeImage
			Object.assign(styled.style ??= {}, patch)
		}
	}))
}

// Writes only the font size. The wrap width (size.width) is managed separately by the corner-resize
// command so it can be scaled in step with the font, preserving the existing line breaks.
export function setCornerResizeInStore(objCtx:ObjectContext, id: string, newFontSize: number): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id)
		if (obj && obj.type !== 'image') {
			if (obj.style) obj.style.fontSize = newFontSize
		}
	}))
}

export function setWrapWidthInStore(objCtx:ObjectContext, id: string, w: number | null): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeText | null
		if (!obj) return
		if (w !== null) {
			if (!obj.size) obj.size = {}
			obj.size.width = w
		} else if (obj.size?.width != null) {
			delete obj.size.width
		}
	}))
}

// Plain text stores its soft line-break positions (character offsets into content) rather than a
// wrap width. An empty array means "no soft wraps", so the key is dropped rather than persisting [].
export function setSoftWrapPositionsInStore(objCtx:ObjectContext, id: string, positions: number[]): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeText | null
		if (!obj) return
		if (positions.length > 0) obj.softWrapPositions = positions
		else delete obj.softWrapPositions
	}))
}

export function setFieldWidthInStore(objCtx:ObjectContext, id: string, left: number, width: number | null): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeText | null
		if (!obj) return
		obj.position = { x: left, y: obj.position?.y ?? 0 }
		if (width != null) {
			if (!obj.size) obj.size = {}
			obj.size.width = width
		} else if (obj.size?.width != null) {
			delete obj.size.width
		}
	}))
}

export function setTextContentInStore(objCtx:ObjectContext, id: string, text: string): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeText | null
		if (obj) obj.content = text
	}))
}

export function setImageSrcInStore(objCtx:ObjectContext, id: string, src: string | null): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeImage | null
		if (!obj) return
		if (src == null) delete obj.src
		else obj.src = src
	}))
}

export function setImageUploadInStore(objCtx: ObjectContext, id: string, upload: UploadedImageData | null): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeImage | null
		if (!obj) return
		delete obj.src  // Clear any env-specific URL previously stored as src
		if (upload == null) delete obj.image
		else obj.image = upload
	}))
}

// Switches an image between a record-bound field source and an uploaded source, clearing
// the now-irrelevant data so the new source shows immediately: switching to a field drops
// any uploaded image / fallback src; switching to upload drops the field path.
export function setImageSourceTypeInStore(objCtx: ObjectContext, id: string, isField: boolean): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeImage | null
		if (!obj) return
		obj.isField = isField
		if (isField) { delete obj.image; delete obj.src }
		else delete obj.field
	}))
}

// Restores an image's full source state (used to undo setImageSourceTypeInStore). Each
// field is written when present and deleted when undefined, mirroring the prior snapshot.
export function restoreImageSourceInStore(
	objCtx: ObjectContext, id: string,
	snap: { isField?: boolean; image?: UploadedImageData; src?: string | null; field?: string },
): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeImage | null
		if (!obj) return
		obj.isField = snap.isField
		if (snap.image == null) delete obj.image; else obj.image = snap.image
		if (snap.src == null)   delete obj.src;   else obj.src   = snap.src
		if (snap.field == null) delete obj.field; else obj.field = snap.field
	}))
}

// Switches a text object between a plain literal (source = plain) and a data-bound template
// field (source = template). Plain and field both store their text in `content`, so the text
// itself is never rewritten and survives the switch unchanged. A field uses size.width as its
// reserved width — plain text has none (it hugs its ink and wraps via softWrapPositions) — so
// the width is written alongside the flag, in one store update: the field's box comes from it
// the moment the object remounts on the flag. `width` null means "no stored width".
export function setTextSourceTypeInStore(objCtx: ObjectContext, id: string, isField: boolean, width: number | null): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id) as RuntimeText | null
		if (!obj || obj.type !== 'text') return
		if (!obj.style) obj.style = {}
		if (isField) obj.style.isField = true
		else delete obj.style.isField
		if (width === null) {
			if (obj.size?.width != null) delete obj.size.width
		} else {
			if (!obj.size) obj.size = {}
			obj.size.width = width
		}
	}))
}

// The reserved width to give a text object whose source is becoming a template field: its own
// stored width when it has one (a field that was switched to plain and back), else the standard
// field default — in both cases reduced to the space its parent has for it, so the reserved box
// (and the open selection drawn around it) can't overhang the container. Called from the source
// control, outside any reactive context, so it reads the child's stored left directly.
export function fieldReserveWidthFor(objCtx: ObjectContext, impls: ImplRegistry, id: string, canvasW: number): number {
	const obj      = findObjectById(objCtx.objects(), id) as RuntimeText | null
	const designW  = obj?.size?.width ?? ADD_TEXT_DEF_W
	const fontSize = (obj?.style?.fontSize as number | undefined) ?? DEFAULT_FONT
	const parentId = objCtx.findParentOf(id)
	const parent   = parentId ? findObjectById(objCtx.objects(), parentId) as RuntimeContainer | null : null
	const parentW  = (parentId ? impls[parentId]?.getSize?.()?.width : undefined) ?? canvasW
	// Only an absolutely-positioned child keeps its own left: in a flex container the layout places
	// it (a full-width box is centred/aligned to fit), so the whole container width is available.
	const layout   = parent?.layout ?? 'absolute'
	const availW   = layout === 'absolute' ? parentW - (obj?.position?.x ?? 0) : parentW
	return fieldReserveWidth(designW, availW, fontSize)
}

export function removeObject(objCtx: ObjectContext, selCtx: SelectionContext) {
	return (id: string): void => {
		removeObjectById(objCtx, id)
		selCtx.deselect()
	}
}

// Reorders `id` one step in z-order within its own sibling array — works for
// top-level objects and for children nested inside a container. Siblings always
// share a coordinate frame, so their bboxes compare directly regardless of depth.
function reorderAmongSiblings(
	objCtx:   ObjectContext,
	impls:    ImplRegistry,
	canvas:   () => Canvas,
	id:       string,
	dir:      'up' | 'down',
): void {
	const siblings = findSiblingArray(objCtx.objects(), id)
	if (!siblings) return
	const i = siblings.findIndex(o => o.id === id)
	if (i < 0) return
	const selBox = getObjectBBox(siblings[i], impls, canvas)
	let j = -1
	if (dir === 'up') {
		if (i === siblings.length - 1) return
		for (let k = i + 1; k < siblings.length; k++) {
			if (rectsOverlap(selBox, getObjectBBox(siblings[k], impls, canvas))) { j = k; break }
		}
	} else {
		if (i <= 0) return
		for (let k = i - 1; k >= 0; k--) {
			if (rectsOverlap(selBox, getObjectBBox(siblings[k], impls, canvas))) { j = k; break }
		}
	}
	if (j < 0) return
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const draft = findSiblingArray(arr, id)
		if (!draft) return
		const [moved] = draft.splice(i, 1)
		draft.splice(j, 0, moved)
	}))
}

export function moveObjectUp(objCtx: ObjectContext, selCtx: SelectionContext, canvasCtx: CanvasContext) {
	return (id: string): void => reorderAmongSiblings(objCtx, selCtx.impls, canvasCtx.canvas, id, 'up')
}

export function moveObjectDown(objCtx: ObjectContext, selCtx: SelectionContext, canvasCtx: CanvasContext) {
	return (id: string): void => reorderAmongSiblings(objCtx, selCtx.impls, canvasCtx.canvas, id, 'down')
}

export function updateSelectedStyle(objCtx: ObjectContext, selCtx: SelectionContext) {
	return (patch: Style): void => {
		const id = selCtx.selected()
		if (!id) return
		objCtx.setObjects(produce((arr: RuntimeObject[]) => {
			const obj = findObjectById(arr, id)
			if (obj && obj.type !== 'image') Object.assign(obj.style ??= {}, patch)
		}))
	}
}

export function setObjectFieldById(objCtx:ObjectContext, id: string, key: string, value: unknown): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id)
		if (obj) (obj as unknown as Record<string, unknown>)[key] = value
	}))
}

// Sets (or clears, when value is empty) a theme-variable binding for a property key.
// Bindings are stored in object.bindings (property key → variable name); an empty value
// removes the entry and drops the bindings object entirely once it has no keys left.
export function setBindingById(objCtx:ObjectContext, id: string, key: string, value: string | undefined): void {
	objCtx.setObjects(produce((arr: RuntimeObject[]) => {
		const obj = findObjectById(arr, id)
		if (!obj) return
		if (value == null || value === '') {
			if (obj.bindings) {
				delete obj.bindings[key]
				if (Object.keys(obj.bindings).length === 0) delete obj.bindings
			}
		} else {
			(obj.bindings ??= {})[key] = value
		}
	}))
}

